import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminShell } from "@/components/AdminShell";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ShieldOff, RefreshCw, KeyRound, Smartphone } from "lucide-react";
import { CodeInput, isCodeReady, RecoveryCodesList, MfaSetupConfirm } from "@/components/MfaWidgets";

const ACTIONS = {
  reset: { label: "Move to a new phone", endpoint: "/auth/mfa/reset", icon: Smartphone },
  codes: { label: "Get new recovery codes", endpoint: "/auth/mfa/recovery-codes", icon: RefreshCw },
  disable: { label: "Remove authenticator", endpoint: "/auth/mfa/disable", icon: ShieldOff },
};

function ConfirmForm({ action, isSuperAdmin, onSuccess, onCancel }) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const saRemove = action === "disable" && isSuperAdmin;
  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post(ACTIONS[action].endpoint, { password, code }); onSuccess(data); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not confirm"); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="mt-4 border border-black/15 bg-zinc-50 p-4" data-testid={`mfa-confirm-form-${action}`}>
      <div className="text-xs font-black uppercase tracking-widest">{ACTIONS[action].label}</div>
      <p className="mt-1 text-xs text-zinc-600">
        {saRemove
          ? "Super Admin: enter your password and one of your recovery codes. That recovery code becomes your sign-in code from now on, until you set up a new authenticator."
          : "Confirm it's you with your password and a current code."}
      </p>
      <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="mfa-confirm-password" className="mt-3 w-full border border-black/20 px-3 py-2 text-sm outline-none focus:border-[#002FA7]" />
      <CodeInput value={code} onChange={setCode} testId="mfa-confirm-code" />
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onCancel} data-testid="mfa-confirm-cancel" className="flex-1 border border-black/30 bg-white px-4 py-2 text-sm hover:bg-black hover:text-white">Cancel</button>
        <button type="submit" disabled={busy || !password || !isCodeReady(code) || (saRemove && /^\d{6}$/.test(code))} data-testid="mfa-confirm-submit" className="flex-1 border border-black bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-800 disabled:opacity-40">
          {busy ? "Checking…" : "Confirm"}
        </button>
      </div>
    </form>
  );
}

function StatusBadge({ status }) {
  if (status.authenticator) return <span className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-emerald-800" data-testid="mfa-status-badge"><ShieldCheck className="h-3 w-3" /> On</span>;
  if (status.static_code_active) return <span className="inline-flex items-center gap-1 border border-amber-400 bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-800" data-testid="mfa-status-badge"><KeyRound className="h-3 w-3" /> Kept recovery code</span>;
  return <span className="inline-flex items-center gap-1 border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-zinc-600" data-testid="mfa-status-badge"><ShieldOff className="h-3 w-3" /> Off</span>;
}

export default function AdminAccount() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [action, setAction] = useState(null);
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState(() => JSON.parse(sessionStorage.getItem("volmee_new_recovery_codes") || "null"));

  const load = () => api.get("/auth/mfa/status").then(({ data }) => setStatus(data)).catch(() => toast.error("Could not load security settings"));
  useEffect(() => { load(); sessionStorage.removeItem("volmee_new_recovery_codes"); }, []);

  async function startSetup() {
    try { const { data } = await api.post("/auth/mfa/setup"); setSetup(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start setup"); }
  }
  function onActionDone(data) {
    if (action === "reset") setSetup(data);
    if (action === "codes") setCodes(data.recovery_codes);
    if (action === "disable") toast.success(data.static_code_active ? "Authenticator removed — sign in with your kept recovery code" : "Authenticator removed");
    setAction(null);
    load();
  }

  return (
    <AdminShell title="Security">
      <main className="mx-auto max-w-3xl px-6 py-10">
        {codes ? <div className="mb-6"><RecoveryCodesList codes={codes} onDone={() => setCodes(null)} /></div> : null}
        <section className="border border-black/10 bg-white p-6 shadow-[6px_6px_0_0_rgba(0,0,0,0.05)]" data-testid="authenticator-settings">
          <div className="text-xs uppercase tracking-widest text-[#002FA7]">Authenticator</div>
          <div className="mt-1 flex items-center gap-3">
            <h1 className="font-display text-3xl font-black tracking-tighter">Two-step codes</h1>
            {status ? <StatusBadge status={status} /> : null}
          </div>
          <p className="mt-2 text-sm text-zinc-600">
            Your authenticator code is needed to permanently delete events{status?.is_super_admin ? " and to sign in as Super Admin" : ""}.
          </p>
          {status?.authenticator ? (
            <>
              <p className="mt-2 text-xs text-zinc-500" data-testid="recovery-remaining">Recovery codes left: <b>{status.recovery_remaining}</b> of 10</p>
              {status.recovery_remaining <= 2 ? (
                <div className="mt-2 border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="recovery-low-warning">
                  {status.recovery_remaining === 0 ? "You have no recovery codes" : "You're running low on recovery codes"} — if you lose your phone you could be locked out. Use <b>Get new recovery codes</b> below.
                </div>
              ) : null}
            </>
          ) : null}

          {status && !setup && !action ? (
            <div className="mt-5 flex flex-wrap gap-2">
              {!status.authenticator ? (
                <button onClick={startSetup} data-testid="mfa-turn-on-btn" className="inline-flex items-center gap-1.5 border border-[#002FA7] bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-[#002FA7]">
                  <ShieldCheck className="h-4 w-4" /> {status.static_code_active ? "Set up a new authenticator" : "Turn on authenticator"}
                </button>
              ) : (
                Object.entries(ACTIONS).map(([key, a]) => (
                  <button key={key} onClick={() => setAction(key)} data-testid={`mfa-action-${key}`} className={`inline-flex items-center gap-1.5 border px-4 py-2 text-sm ${key === "disable" ? "border-red-300 text-red-700 hover:bg-red-600 hover:text-white" : "border-black/20 hover:bg-black hover:text-white"}`}>
                    <a.icon className="h-4 w-4" /> {a.label}
                  </button>
                ))
              )}
            </div>
          ) : null}

          {action ? <ConfirmForm action={action} isSuperAdmin={!!status?.is_super_admin} onSuccess={onActionDone} onCancel={() => setAction(null)} /> : null}
          {setup ? (
            <div className="mt-5 max-w-sm">
              <p className="text-xs text-zinc-600">Scan this with Google Authenticator, Authy or 1Password, then enter the 6-digit code it shows.</p>
              <MfaSetupConfirm setup={setup} onEnabled={(c) => { setCodes(c); setSetup(null); load(); }} />
              <button onClick={() => setSetup(null)} data-testid="mfa-setup-cancel" className="mt-2 w-full text-xs text-zinc-600 underline">Cancel</button>
            </div>
          ) : null}
        </section>
      </main>
    </AdminShell>
  );
}
